import { Core } from "@faire/design-tokens";
import { optimizeProductImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import React from "react";
import styled from "styled-components";

import { OrderNumber } from "./OrderNumber";

export const OrderItemCard: React.FC<
  Partial<ISelfServeBrandOrder> & {
    isRetailer: boolean;
    onClick?: () => void;
    innerRef?: React.RefObject<HTMLDivElement>;
  }
> = ({
  brand_name,
  retailer_name,
  order_placed_string,
  summary_string,
  brand_order,
  isRetailer,
  onClick,
  order_number,
  status_string,
  innerRef,
}) => {
  const productImage = brand_order?.order_items?.[0]?.product_image;

  const { isMobile } = useViewport();

  return (
    <OrderItemCardWrapper
      onClick={onClick}
      data-test-id={`order-item-number-${order_number}`}
      ref={innerRef}
    >
      {productImage ? (
        <>
          <ItemImage
            src={optimizeProductImageUrl(
              productImage,
              false,
              isMobile ? 80 : 100
            )}
            alt="Order Item image"
          />
          {isMobile ? <Spacer width="2x" /> : <Spacer height="3x" />}
        </>
      ) : null}
      <Column>
        {/* Show retailer name on each order item card for brand, vice versa */}
        <Typography variant="subheadingSansMedium">
          {isRetailer ? brand_name : retailer_name}
        </Typography>
        <Spacer height="1/2x" />
        <OrderNumber />
        <Spacer height="1/2x" />
        <Row>
          <Typography variant="paragraphSansMedium">
            <StrictLocalMsg
              defaultMessage="Total"
              description={{
                text: "This is title of the total amount of an order",
              }}
            />
          </Typography>
          <Spacer width="1/2x" />
          <Typography>{summary_string}</Typography>
        </Row>
        <Spacer height="1/2x" />
        {status_string ? (
          <Typography>{status_string}</Typography>
        ) : (
          <Typography>{order_placed_string}</Typography>
        )}
      </Column>
    </OrderItemCardWrapper>
  );
};

export const OrderItemCardWrapper = styled(Row).attrs({
  justify: "flex-start",
  align: "center",
})`
  border: 1px solid ${Core.border.muted};
  border-radius: 4px;
  padding: ${getSpacing("2x")};
  &:hover {
    border: 1px solid ${Core.border.default};
  }
  min-height: 120px;
  min-width: 326px;
  height: auto;
  width: 100%;
  cursor: pointer;
`;

const ItemImage = styled.img`
  height: 100px;
  width: 100px;
  ${responsiveMediaQueries.mobile} {
    height: 80px;
    width: 80px;
  }
`;
